<template>
  <a-drawer
    :visible="visible"
    title="Configura las columnas a mostrar"
    destroy-on-close
    placement="right"
    width="480"
    :body-style="{ padding: 0, height: 'calc(100% - 55px)' }"
    :maskClosable="false"
    @close="handleCloseDrawer"
  >
    <custom-scrollbar height="calc(100% - 72px)">
      <div class="modal__body">
        <p class="drawer__body--subtitle">
          Selecciona las columnas que deseas mostrar en la tabla.
        </p>

        <a-input-search
          v-model:value="search"
          placeholder="Buscar por nombre de la columna"
          allowClear
        />

        <p class="columns-title">
          Columnas a mostrar:
          <a-tooltip>
            <template #title>
              {{ titleColumns }}
            </template>
            <InfoCircleOutlined style="margin-left: 5px" />
          </a-tooltip>
        </p>

        <a-checkbox
          v-model:checked="state.checkAll"
          :indeterminate="state.indeterminate"
          @change="onCheckAllChange"
          style="margin-bottom: 5px"
        >
          Seleccionar todos
        </a-checkbox>
        <br />

        <a-checkbox-group
          :value="state.checkedList"
          @change="onCheckboxGroupChange"
          :options="columnsCheckboxFilters"
          class="vertical-checkbox-group"
        />
        <br />
      </div>
    </custom-scrollbar>

    <div class="footer">
      <div class="details-selected">
        {{ state?.checkedList?.length }}/{{ columnsCheckbox?.length }} columnas seleccionadas
      </div>
      <div>
        <a-button class="footer__button" @click="handleCloseDrawer"> Cancelar </a-button>
        <a-button type="primary" @click="handleApplyFilters" :disabled="changeColumn">
          Guardar y aplicar
        </a-button>
      </div>
    </div>
  </a-drawer>
</template>
<script setup lang="ts">
import { computed, ref, createVNode, reactive, watch, onMounted } from "vue";
import { Modal, message } from "ant-design-vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";
import { updateTablePreferences } from "@/app/shared/services";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { isEqual } from "lodash";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { fetchTablePreferences } from "@/app/shared/services";

const props = withDefaults(
  defineProps<{
    visible: boolean;
    columns: Array;
    value: Array;
    scope: string;
    titleColumns: string;
  }>(),
  {
    visible: false,
    columns: [],
    value: [],
  }
);

const emit = defineEmits<{
  "update:visible": [visible: boolean];
  "update:value": [value: Array];
}>();

const search = ref("");
const changeColumn = ref(true);

const columnsCheckbox = computed(() =>
  props.columns
    .filter((el) => el.code)
    // .filter((el) => (search == "" || el.title.toLowerCase().includes(search.value.toLowerCase())))
    .map((item) => ({
      value: item?.code,
      label: item?.column_name,
      disabled: !item?.is_editable,
    }))
    .sort((a, b) => {
      if (a.disabled && !b.disabled) return -1;
      if (!a.disabled && b.disabled) return 1;
      return 0;
    })
);

const columnsCheckboxFilters = computed(() =>
  columnsCheckbox.value.filter(
    (el) =>
      // el.value != "actions" &&
      search == "" || el.label.toLowerCase().includes(search.value.toLowerCase())
  )
);

const state = reactive({
  indeterminate: true,
  checkAll: true,
  checkedList: [],
  initialChecked: [],
});

const handleApplyFilters = async () => {
  try {
    const ids = state.checkedList
      // .filter((el) => el != "actions")
      .map((code) => {
        const match = props.columns.find((item) => item.code === code);
        return match ? match.id : null;
      });

    await updateTablePreferences(props.scope, ids);
    message.success("Se ha configurado las columnas correctamente");
    emit("update:value", state.checkedList);
    emit("update:visible", false);
    state.initialChecked = state.checkedList;
    changeColumn.value = true;
  } catch (e) {
    message.error("Hubo un error");
  }
};

const handleCloseDrawer = () => {
  if (isEqual(props.value, state.checkedList)) {
    emit("update:visible", false);
  } else {
    Modal.confirm({
      title: `¿Deseas descartar los cambios realizados?`,
      content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
      icon: createVNode(QuestionCircleOutlined),
      centered: true,
      onOk: () => emit("update:visible", false),
      okText: "Sí, descartar cambios",
      cancelText: "No, seguir editando",
    });
  }
};

const handleClearFilters = () => {
  state.checkedList = props.columns.map((el) => el.code);
  state.checkAll = true;
};

const onCheckAllChange = (e: any) => {
  if (search.value == "") {
    Object.assign(state, {
      checkedList: e.target.checked
        ? columnsCheckbox.value.map((el) => el.value)
        : columnsCheckbox.value.filter((el) => el.disabled).map((el) => el.value),
      indeterminate: false,
    });
  } else {
    if (e.target.checked) {
      const checkedList = columnsCheckboxFilters.value.map((el) => el.value);
      state.checkedList = [...new Set([...state.checkedList, ...checkedList])];
    } else {
      state.checkedList = state.checkedList.filter(
        (el) => !columnsCheckboxFilters.value.some((obj) => el == obj.value)
      );
    }
  }
  changeColumn.value = false;
};

const onCheckboxGroupChange = (val) => {
  const filteredValues = columnsCheckboxFilters.value.map((option) => option.value);
  state.checkedList = [
    ...new Set([...state.checkedList.filter((item) => !filteredValues.includes(item)), ...val]),
  ];
  changeColumn.value = false;
};

watch(
  () => state.checkedList,
  (val) => {
    if (search.value == "") {
      state.indeterminate = !!val.length && val.length < columnsCheckbox.value.length;
      state.checkAll = val.length === columnsCheckbox.value.length;
    } else {
      const filtersItems = val.filter((el) =>
        columnsCheckboxFilters.value.some((obj) => el == obj.value)
      );
      state.indeterminate =
        !!filtersItems.length && filtersItems.length < columnsCheckboxFilters.value.length;
      state.checkAll = filtersItems.length === columnsCheckboxFilters.value.length;
    }
    if (JSON.stringify(state.checkedList.sort()) === JSON.stringify(state.initialChecked.sort())) {
      changeColumn.value = true;
    }
  }
);

watch(
  () => props.value,
  (val) => {
    state.checkedList = val;
    state.initialChecked = val;
  },
  { immediate: true }
);
</script>

<style lang="sass" scoped>
.modal
  &__body
    margin: 12px 24px

.footer
  display: flex
  align-items: center
  justify-content: space-between
  margin-top: 8px
  padding: 12px 16px
  border-top: $border-normal

  &__display
    width: 100%
    font-size: 12px
    color: $gray-7

  &__button
    margin-right: 12px

.drawer__body
  &--title
    font-weight: 600
    color: $gray-9
    margin-bottom: 0

  &--subtitle
    font-weight: 400
    color: $gray-8

.container__empty
  text-align: center
  display: flex
  flex-direction: column
  height: calc(100% - 60px)
  align-items: center
  justify-content: center

  img
    width: 100px

.text--bottom
  margin-top: 0

.text--top
  margin-top: 20px
  margin-bottom: 0

.select_identifiers
  display: flex
  align-items: center
  img
    margin-right: 8px

:deep(.vertical-checkbox-group .ant-checkbox-wrapper)
  display: flex !important
  width: 100% !important
  margin: 5px 0

.columns-title
  font-weight: 500
  margin-top: 15px

.details-selected
  font-style: italic
  color: $gray-6
</style>
